/**
 * Business Health — P&L, menu profitability, break-even, time analysis.
 * Owner/admin only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { formatAmd } from '../locale.js';
import { supabase } from '../supabase.js';
import { fetchPaged } from '../fetchPaged.js';
import {
  EXPENSE_CATEGORIES,
  type MenuQuadrant,
  type MonthlyExpenseCategory,
  menuQuadrant,
  monthEndIso,
  monthStart,
  monthStartIso,
  pct,
} from '../lib/businessHealth.js';
import { loadCogsContext, loadLedgerCogs, orderLineCogs } from '../lib/actualCogs.js';
import { lineRevenue } from '../lib/recipeCost.js';
import { HelpCircle, Percent, PieChart, Receipt, Users, Wallet } from 'lucide-react';
import {
  Callout,
  CardHeader,
  CHART_TOOLTIP,
  IconButton,
  Modal,
  PageHeader,
  Stat,
  Stats,
} from '../ui/index.js';

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

const TS = CHART_TOOLTIP;

const QUAD_COLORS: Record<MenuQuadrant, string> = {
  star: 'var(--chart-2)',
  workhorse: 'var(--chart-1)',
  puzzle: 'var(--chart-3)',
  dog: 'var(--chart-4)',
};

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const QUAD_I18N: Record<MenuQuadrant, { label: string; hint: string }> = {
  star: { label: 'businessHealth.star', hint: 'businessHealth.starHint' },
  workhorse: { label: 'businessHealth.workhorse', hint: 'businessHealth.workhorseHint' },
  puzzle: { label: 'businessHealth.puzzle', hint: 'businessHealth.puzzleHint' },
  dog: { label: 'businessHealth.dog', hint: 'businessHealth.dogHint' },
};

const EXPENSE_I18N: Record<MonthlyExpenseCategory, string> = {
  labor: 'businessHealth.catLabor',
  rent: 'businessHealth.catRent',
  utilities: 'businessHealth.catUtilities',
  marketing: 'businessHealth.catMarketing',
  other: 'businessHealth.catOther',
};

const COST_I18N = {
  cogs: 'businessHealth.costCogs',
  labor: 'businessHealth.costLabor',
  rent: 'businessHealth.costRent',
  utilities: 'businessHealth.costUtilities',
  drawer: 'businessHealth.costDrawer',
  marketing: 'businessHealth.costMarketing',
  other: 'businessHealth.costOther',
} as const;

type BiggestCostKey = keyof typeof COST_I18N;

function MetricHelp({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        label={t('businessHealth.helpHow', { title })}
        size="sm"
        onClick={() => setOpen(true)}
      >
        <HelpCircle />
      </IconButton>
      <Modal open={open} onClose={() => setOpen(false)} title={title}>
        <div className="metric-help-body">{children}</div>
      </Modal>
    </>
  );
}

function ChartTitleWithHelp({ title, help }: { title: string; help: React.ReactNode }) {
  return <CardHeader title={title} right={<MetricHelp title={title}>{help}</MetricHelp>} />;
}

interface ProductProfit {
  id: string;
  name: string;
  qty: number;
  revenue: number;
  cogs: number;
  profit: number;
  marginPct: number;
  popularityPct: number;
  quadrant: MenuQuadrant;
}

interface HealthData {
  revenue: number;
  /** P&L food cost — sum of per-line COGS on paid orders (aligned with revenue). */
  cogs: number;
  /** Inventory ledger COGS (physical deductions); shown as reconciliation only. */
  ledgerCogs: number;
  grossProfit: number;
  grossMarginPct: number;
  orders: number;
  covers: number;
  avgOrder: number;
  revenuePerCover: number;
  labor: number;
  rent: number;
  utilities: number;
  marketing: number;
  otherFixed: number;
  drawerExpenses: number;
  totalOpex: number;
  netProfit: number;
  netMarginPct: number;
  cashBalance: number;
  inventoryValue: number;
  daysOfInventory: number;
  products: ProductProfit[];
  hourly: { hour: string; revenue: number }[];
  weekday: { day: string; revenue: number; idx: number }[];
  biggestCostKey: BiggestCostKey | null;
  biggestCostAmount: number;
}

function emptyExpenses(): Record<MonthlyExpenseCategory, number> {
  return { labor: 0, rent: 0, utilities: 0, marketing: 0, other: 0 };
}

export function BusinessHealthView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [month, setMonth] = useState(monthStart(new Date()));
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expenseForm, setExpenseForm] = useState(emptyExpenses());
  const [cashBalance, setCashBalance] = useState('0');
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    if (!supabase || !clientId) return;
    const db = supabase;
    setLoading(true);
    setError(null);

    const fromIso = monthStartIso(month);
    const toIso = monthEndIso(month);

    try {
      // Recipe/ingredient context first — the ledger valuation needs ingredient costs.
      const cogsCtx = await loadCogsContext(db, clientId);

      const [orders, items, ledger, productsRes, ingsRes, monthlyRes, settingsRes, drawerRes] =
        await Promise.all([
          fetchPaged((offset, limit) =>
            db
              .from('orders')
              .select('total, pax, paid_at')
              .eq('client_id', clientId)
              .eq('status', 'paid')
              .gte('paid_at', fromIso)
              .lte('paid_at', toIso)
              .order('paid_at', { ascending: true })
              .range(offset, limit),
          ),
          fetchPaged((offset, limit) =>
            db
              .from('order_items')
              .select(
                'product_id, name, unit_price, quantity, discount, modifiers, cogs_amd, orders!inner(status, paid_at, client_id)',
              )
              .eq('client_id', clientId)
              .filter('orders.status', 'eq', 'paid')
              .filter('orders.paid_at', 'gte', fromIso)
              .filter('orders.paid_at', 'lte', toIso)
              .filter('orders.client_id', 'eq', clientId)
              .order('id', { ascending: true })
              .range(offset, limit),
          ),
          loadLedgerCogs(db, clientId, fromIso, toIso, cogsCtx.ingredientCost),
          db.from('products').select('id, name').eq('client_id', clientId),
          db.from('ingredients').select('stock, cost_price').eq('client_id', clientId),
          db.from('monthly_expenses').select('category, amount').eq('client_id', clientId).eq('month', month),
          db.from('business_settings').select('cash_balance').eq('client_id', clientId).maybeSingle(),
          fetchPaged((offset, limit) =>
            db
              .from('shift_expenses')
              .select('amount, created_at')
              .eq('client_id', clientId)
              .gte('created_at', fromIso)
              .lte('created_at', toIso)
              .order('created_at', { ascending: true })
              .range(offset, limit),
          ),
        ]);

      const productNames = new Map((productsRes.data ?? []).map((p) => [p.id, p.name]));

      let revenue = 0;
      let covers = 0;
      const hourlyMap = new Map<number, number>();
      const weekdayMap = new Map<number, number>();

      for (const o of orders) {
        revenue += Number(o.total);
        covers += Number(o.pax ?? 1);
        if (o.paid_at) {
          const d = new Date(o.paid_at);
          hourlyMap.set(d.getHours(), (hourlyMap.get(d.getHours()) ?? 0) + Number(o.total));
          weekdayMap.set(d.getDay(), (weekdayMap.get(d.getDay()) ?? 0) + Number(o.total));
        }
      }

      type ItemRow = {
        product_id: string;
        name: string;
        unit_price: number;
        quantity: number;
        discount: number | null;
        modifiers: unknown;
        cogs_amd: number | null;
      };
      const itemRows = items as unknown as ItemRow[];

      // Per-product COGS: frozen cogs_amd from order close, or estimated for legacy rows.
      const prodAgg = new Map<string, { qty: number; revenue: number; cogs: number; name: string }>();
      let itemsCogs = 0;
      for (const it of itemRows) {
        const rev = lineRevenue(it.unit_price, it.quantity, it.discount);
        const itemCogs = orderLineCogs(
          cogsCtx,
          it.product_id,
          Number(it.quantity),
          it.modifiers,
          it.cogs_amd,
        );
        itemsCogs += itemCogs;
        const name = productNames.get(it.product_id) ?? it.name;
        const e = prodAgg.get(it.product_id) ?? { qty: 0, revenue: 0, cogs: 0, name };
        e.qty += Number(it.quantity);
        e.revenue += rev;
        e.cogs += itemCogs;
        prodAgg.set(it.product_id, e);
      }

      // P&L COGS: per sold line (recipe + modifiers + snapshotted cogs_amd when available).
      const cogs = itemsCogs;
      const ledgerCogs = ledger.net;

      const totalQty = [...prodAgg.values()].reduce((s, p) => s + p.qty, 0);
      const products: ProductProfit[] = [...prodAgg.entries()]
        .map(([id, p]) => {
          const profit = p.revenue - p.cogs;
          const marginPct = p.revenue > 0 ? pct(profit, p.revenue) : 0;
          const popularityPct = totalQty > 0 ? pct(p.qty, totalQty) : 0;
          return {
            id,
            name: p.name,
            qty: p.qty,
            revenue: p.revenue,
            cogs: p.cogs,
            profit,
            marginPct,
            popularityPct,
            quadrant: menuQuadrant(popularityPct, marginPct),
          };
        })
        .sort((a, b) => b.profit - a.profit);

      const expenses = emptyExpenses();
      for (const row of monthlyRes.data ?? []) {
        const cat = row.category as MonthlyExpenseCategory;
        if (cat in expenses) expenses[cat] += Number(row.amount);
      }

      const drawerExpenses = (drawerRes as { amount: number }[]).reduce(
        (s, e) => s + Number(e.amount),
        0,
      );

      const grossProfit = revenue - cogs;
      const totalOpex =
        expenses.labor +
        expenses.rent +
        expenses.utilities +
        expenses.marketing +
        expenses.other +
        drawerExpenses;
      const netProfit = grossProfit - totalOpex;

      const costLines: { key: BiggestCostKey; amount: number }[] = [
        { key: 'cogs' as const, amount: cogs },
        { key: 'labor' as const, amount: expenses.labor },
        { key: 'rent' as const, amount: expenses.rent },
        { key: 'utilities' as const, amount: expenses.utilities },
        { key: 'drawer' as const, amount: drawerExpenses },
        { key: 'marketing' as const, amount: expenses.marketing },
        { key: 'other' as const, amount: expenses.other },
      ].sort((a, b) => b.amount - a.amount);
      const biggest = costLines[0] ?? { key: null as BiggestCostKey | null, amount: 0 };

      const inventoryValue = (ingsRes.data ?? []).reduce(
        (s, i) => s + Number(i.stock ?? 0) * Number(i.cost_price ?? 0),
        0,
      );
      const daysInMonth = new Date(new Date(month).getFullYear(), new Date(month).getMonth() + 1, 0).getDate();
      const dailyCogs = cogs / Math.max(1, daysInMonth);
      const daysOfInventory = dailyCogs > 0 ? Math.round(inventoryValue / dailyCogs) : 0;

      const balance = Number(settingsRes.data?.cash_balance ?? 0);
      setCashBalance(String(balance));
      setExpenseForm(expenses);

      setData({
        revenue,
        cogs,
        ledgerCogs,
        grossProfit,
        grossMarginPct: pct(grossProfit, revenue),
        orders: orders.length,
        covers,
        avgOrder: orders.length > 0 ? revenue / orders.length : 0,
        revenuePerCover: covers > 0 ? revenue / covers : 0,
        labor: expenses.labor,
        rent: expenses.rent,
        utilities: expenses.utilities,
        marketing: expenses.marketing,
        otherFixed: expenses.other,
        drawerExpenses,
        totalOpex,
        netProfit,
        netMarginPct: pct(netProfit, revenue),
        cashBalance: balance,
        inventoryValue,
        daysOfInventory,
        products,
        hourly: [...hourlyMap.entries()]
          .sort((a, b) => a[0] - b[0])
          .map(([h, rev]) => ({ hour: `${String(h).padStart(2, '0')}h`, revenue: rev })),
        weekday: [...weekdayMap.entries()]
          .sort((a, b) => a[0] - b[0])
          .map(([idx, rev]) => ({ day: WEEKDAY_KEYS[idx] ?? String(idx), revenue: rev, idx })),
        biggestCostKey: biggest.key,
        biggestCostAmount: biggest.amount,
      });
      setLoading(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '__load_failed__');
      setLoading(false);
    }
  }, [clientId, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const breakEven = useMemo(() => {
    if (!data) return null;
    const daysInMonth = new Date(new Date(month).getFullYear(), new Date(month).getMonth() + 1, 0).getDate();
    const fixedMonthly =
      data.labor + data.rent + data.utilities + data.marketing + data.otherFixed + data.drawerExpenses;
    const dailyFixed = fixedMonthly / daysInMonth;
    const marginRate = data.grossMarginPct / 100;
    const dailyBreakEvenRev = marginRate > 0 ? dailyFixed / marginRate : 0;
    const coversPerDay = data.revenuePerCover > 0 ? dailyBreakEvenRev / data.revenuePerCover : 0;
    const runwayMonths =
      data.netProfit < 0 && data.cashBalance > 0
        ? data.cashBalance / Math.abs(data.netProfit)
        : null;
    return { dailyFixed, dailyBreakEvenRev, coversPerDay, runwayMonths, fixedMonthly };
  }, [data, month]);

  const dogs = data?.products.filter((p) => p.quadrant === 'dog').slice(0, 5) ?? [];

  async function saveSettings() {
    if (!supabase || !clientId) return;
    setSavingSettings(true);
    setError(null);
    const balance = Number(cashBalance) || 0;

    const { error: settingsErr } = await supabase.from('business_settings').upsert({
      client_id: clientId,
      cash_balance: balance,
    });
    if (settingsErr) {
      setError(settingsErr.message);
      setSavingSettings(false);
      return;
    }

    for (const cat of EXPENSE_CATEGORIES) {
      const amount = Number(expenseForm[cat]) || 0;
      const { error: expErr } = await supabase.from('monthly_expenses').upsert(
        { client_id: clientId, month, category: cat, amount },
        { onConflict: 'client_id,month,category' },
      );
      if (expErr) {
        setError(expErr.message);
        setSavingSettings(false);
        return;
      }
    }

    setSavingSettings(false);
    await load();
  }

  const displayError =
    error === '__load_failed__' ? t('businessHealth.loadFailed') : error;

  const plLines: [string, number, string][] = data
    ? [
        [t('businessHealth.lineRevenue'), data.revenue, 'var(--text)'],
        [t('businessHealth.lineCogs'), data.cogs, 'var(--danger)'],
        [t('businessHealth.lineGross'), data.grossProfit, 'var(--success)'],
        [t('businessHealth.lineLabor'), data.labor, 'var(--danger)'],
        [t('businessHealth.lineRent'), data.rent, 'var(--danger)'],
        [t('businessHealth.lineUtilities'), data.utilities, 'var(--danger)'],
        [t('businessHealth.lineDrawer'), data.drawerExpenses, 'var(--danger)'],
        [t('businessHealth.lineMarketing'), data.marketing, 'var(--danger)'],
        [t('businessHealth.lineOther'), data.otherFixed, 'var(--danger)'],
        [
          t('businessHealth.lineNet'),
          data.netProfit,
          data.netProfit >= 0 ? 'var(--success)' : 'var(--danger)',
        ],
      ]
    : [];

  const weekdayChart =
    data?.weekday.map((w) => ({
      ...w,
      day:
        w.idx >= 0 && w.idx < WEEKDAY_KEYS.length
          ? t(`businessHealth.${WEEKDAY_KEYS[w.idx]}`)
          : w.day,
    })) ?? [];

  return (
    <div className="sales-page">
      <PageHeader
        title={t('businessHealth.title')}
        subtitle={t('businessHealth.subtitle')}
        actions={
          <input
            type="month"
            aria-label={t('businessHealth.monthAria')}
            value={month.slice(0, 7)}
            onChange={(e) => setMonth(e.target.value + '-01')}
          />
        }
      />

      {loading && (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      )}
      {displayError && (
        <Callout tone="danger" title={t('businessHealth.couldntLoad')}>
          {displayError}
        </Callout>
      )}

      {!loading && data && (
        <>
          <div className={data.netProfit >= 0 ? 'verdict verdict--good' : 'verdict verdict--bad'}>
            <div>
              <div className="verdict-label">
                {data.netProfit >= 0
                  ? t('businessHealth.profitable')
                  : t('businessHealth.losing')}
              </div>
              <div className="verdict-value">
                {data.netProfit >= 0 ? '+' : '−'}
                {AMD(Math.abs(data.netProfit))}
              </div>
            </div>
            {data.netProfit < 0 && data.biggestCostAmount > 0 && data.biggestCostKey && (
              <p className="verdict-note">
                {t('businessHealth.biggestCost', {
                  label: t(COST_I18N[data.biggestCostKey]),
                  amount: AMD(data.biggestCostAmount),
                  pct: fmtPct(pct(data.biggestCostAmount, data.revenue)),
                })}
              </p>
            )}
          </div>

          <Stats>
            <Stat icon={<Wallet />} label={t('businessHealth.revenue')} value={AMD(data.revenue)} />
            <Stat
              icon={<PieChart />}
              label={t('businessHealth.grossMargin')}
              value={fmtPct(data.grossMarginPct)}
            />
            <Stat
              icon={<Percent />}
              label={t('businessHealth.netMargin')}
              value={fmtPct(data.netMarginPct)}
            />
            <Stat icon={<Users />} label={t('businessHealth.covers')} value={data.covers} />
            <Stat icon={<Receipt />} label={t('businessHealth.paidOrders')} value={data.orders} />
          </Stats>

          {/* P&L */}
          <div className="dash-grid" style={{ marginBottom: 16 }}>
            <div className="chart-card">
              <ChartTitleWithHelp
                title={t('businessHealth.plTitle')}
                help={
                  <>
                    <p style={{ margin: '0 0 10px' }}>{t('businessHealth.helpRevenue')}</p>
                    <p style={{ margin: '0 0 10px' }}>
                      {t('businessHealth.helpCogs')} {t('businessHealth.helpCogsExtra')}
                    </p>
                    <p style={{ margin: '0 0 10px' }}>{t('businessHealth.helpGross')}</p>
                    <p style={{ margin: 0 }}>{t('businessHealth.helpLedger')}</p>
                  </>
                }
              />
              <div className="quality-list">
                {plLines.map(([label, val, color]) => (
                  <div key={String(label)} className="quality-row">
                    <span className="quality-label">{label}</span>
                    <span className="quality-value" style={{ color: color as string, fontWeight: 600 }}>
                      {AMD(Number(val))}
                      {Number(val) > 0 &&
                        data.revenue > 0 &&
                        String(label).startsWith('−') && (
                          <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                            {fmtPct(pct(Number(val), data.revenue))}
                          </span>
                        )}
                    </span>
                  </div>
                ))}
              </div>
              {(() => {
                const base = Math.max(data.cogs, data.ledgerCogs);
                if (base <= 0) return null;
                const diffPct = (Math.abs(data.cogs - data.ledgerCogs) / base) * 100;
                const warn = diffPct > 5;
                return (
                  <p
                    className="muted"
                    style={{
                      fontSize: 12,
                      margin: '8px 0 0',
                      color: warn ? 'var(--warning, #dd6b20)' : undefined,
                    }}
                  >
                    {t('businessHealth.ledgerCheck', {
                      ledger: AMD(data.ledgerCogs),
                      cogs: AMD(data.cogs),
                      pct: diffPct.toFixed(1),
                    })}
                    {warn ? t('businessHealth.ledgerWarn') : t('businessHealth.ledgerOk')}
                  </p>
                );
              })()}
            </div>

            <div className="chart-card">
              <div className="chart-title">{t('businessHealth.breakEven')}</div>
              {breakEven && (
                <div className="quality-list">
                  <div className="quality-row">
                    <span className="quality-label">{t('businessHealth.fixedCosts')}</span>
                    <span className="quality-value">{AMD(breakEven.fixedMonthly)}</span>
                  </div>
                  <div className="quality-row">
                    <span className="quality-label">{t('businessHealth.revenueNeeded')}</span>
                    <span className="quality-value">{AMD(breakEven.dailyBreakEvenRev)}</span>
                  </div>
                  <div className="quality-row">
                    <span className="quality-label">{t('businessHealth.coversNeeded')}</span>
                    <span className="quality-value">
                      {breakEven.coversPerDay > 0
                        ? Math.ceil(breakEven.coversPerDay)
                        : t('common.emptyDash')}
                    </span>
                  </div>
                  <div className="quality-row">
                    <span className="quality-label">{t('businessHealth.inventoryOnHand')}</span>
                    <span className="quality-value">
                      {AMD(data.inventoryValue)}
                      {data.daysOfInventory > 0 && (
                        <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                          {t('businessHealth.inventoryDays', { days: data.daysOfInventory })}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="quality-row">
                    <span className="quality-label">{t('businessHealth.cashBalance')}</span>
                    <span className="quality-value">{AMD(data.cashBalance)}</span>
                  </div>
                  {breakEven.runwayMonths != null && (
                    <div className="quality-row">
                      <span className="quality-label">{t('businessHealth.cashRunway')}</span>
                      <span className="quality-value" style={{ color: 'var(--danger)' }}>
                        {t('businessHealth.cashRunwayValue', {
                          months: breakEven.runwayMonths.toFixed(1),
                        })}
                      </span>
                    </div>
                  )}
                  {data.netProfit >= 0 && (
                    <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                      {t('businessHealth.profitableNote')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Expenses entry */}
          <div className="chart-card" style={{ marginBottom: 16 }}>
            <div className="chart-title">{t('businessHealth.monthlyCosts')}</div>
            <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
              {t('businessHealth.monthlyHint')}
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 12,
              }}
            >
              {EXPENSE_CATEGORIES.map((cat) => (
                <label key={cat}>
                  <span style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    {t(EXPENSE_I18N[cat])}
                  </span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    value={expenseForm[cat] || ''}
                    onChange={(e) =>
                      setExpenseForm((f) => ({ ...f, [cat]: Number(e.target.value) || 0 }))
                    }
                  />
                </label>
              ))}
              <label>
                <span style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  {t('businessHealth.cashOnHand')}
                </span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={cashBalance}
                  onChange={(e) => setCashBalance(e.target.value)}
                />
              </label>
            </div>
            <button
              type="button"
              className="btn-primary"
              style={{ marginTop: 12 }}
              disabled={savingSettings}
              onClick={() => void saveSettings()}
            >
              {savingSettings ? t('common.saving') : t('common.save')}
            </button>
          </div>

          {/* Menu engineering */}
          <div className="dash-grid" style={{ marginBottom: 16 }}>
            <div className="chart-card insights-wide">
              <ChartTitleWithHelp
                title={t('businessHealth.menuProfit')}
                help={
                  <>
                    <p style={{ margin: '0 0 10px' }}>{t('businessHealth.helpMenuAgg')}</p>
                    <p style={{ margin: '0 0 10px' }}>{t('businessHealth.helpMenuQty')}</p>
                    <p style={{ margin: '0 0 10px' }}>{t('businessHealth.helpMenuMargin')}</p>
                    <p style={{ margin: 0 }}>{t('businessHealth.helpMenuWine')}</p>
                  </>
                }
              />
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('businessHealth.colProduct')}</th>
                    <th>{t('businessHealth.colType')}</th>
                    <th style={{ textAlign: 'right' }}>{t('businessHealth.colQty')}</th>
                    <th style={{ textAlign: 'right' }}>{t('businessHealth.colProfit')}</th>
                    <th style={{ textAlign: 'right' }}>{t('businessHealth.colMargin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.slice(0, 15).map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td>
                        <span
                          className="badge badge-muted"
                          style={{ background: QUAD_COLORS[p.quadrant], color: '#1d1d1f' }}
                        >
                          {t(QUAD_I18N[p.quadrant].label)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>{p.qty.toFixed(0)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{AMD(p.profit)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtPct(p.marginPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="chart-card">
              <div className="chart-title">{t('businessHealth.menuMatrix')}</div>
              <ResponsiveContainer width="100%" height={220}>
                <ScatterChart margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    type="number"
                    dataKey="popularityPct"
                    name={t('businessHealth.popularity')}
                    unit="%"
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="marginPct"
                    name={t('businessHealth.margin')}
                    unit="%"
                    tick={{ fontSize: 10 }}
                  />
                  <ZAxis type="number" dataKey="profit" range={[40, 200]} />
                  <Tooltip
                    content={({ payload }) => {
                      const p = payload?.[0]?.payload as ProductProfit | undefined;
                      if (!p) return null;
                      return (
                        <div className="chart-tooltip">
                          <div className="chart-tooltip-title">{p.name}</div>
                          <div>{t(QUAD_I18N[p.quadrant].label)}</div>
                          <div>
                            {t('businessHealth.colProfit')}: {AMD(p.profit)}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={data.products} fill="var(--info)">
                    {data.products.map((p) => (
                      <Cell key={p.id} fill={QUAD_COLORS[p.quadrant]} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              {dogs.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <p className="muted" style={{ fontSize: 12, margin: '0 0 4px' }}>
                    {t('businessHealth.considerRemoving')}
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    {dogs.map((p) => (
                      <li key={p.id}>
                        {p.name} — {t(QUAD_I18N.dog.hint)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Time analysis */}
          <div className="dash-grid">
            <div className="chart-card">
              <div className="chart-title">{t('businessHealth.revenueByHour')}</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.hourly}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis hide />
                  <Tooltip
                    formatter={(v: unknown) => [AMD(Number(v)), t('businessHealth.revenue')]}
                    contentStyle={TS}
                  />
                  <Bar dataKey="revenue" fill="var(--info)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-card">
              <div className="chart-title">{t('businessHealth.revenueByWeekday')}</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weekdayChart}>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis hide />
                  <Tooltip
                    formatter={(v: unknown) => [AMD(Number(v)), t('businessHealth.revenue')]}
                    contentStyle={TS}
                  />
                  <Bar dataKey="revenue" fill="var(--info)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              {breakEven && (
                <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                  {t('businessHealth.belowBreakEven', {
                    amount: AMD(breakEven.dailyBreakEvenRev),
                  })}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

